import { useBranding } from '@utazzo/ui';
import { ShieldCheck } from 'lucide-react';

import type { ReactNode } from 'react';

/**
 * Minimal centered auth layout for login / forgot / reset.
 *
 * A single centered card on a plain background: brand logo + name, the title,
 * an optional subtitle, the form, and an optional footer hint. No marketing
 * panel or stats - just a clean, focused sign-in surface.
 *
 * Branding (app name + logo) is read from AppConfig, so swapping brands
 * doesn't require touching these screens.
 */

interface AuthLayoutProps {
  title: string;
  subtitle?: ReactNode;
  /** Optional small action/link rendered above the title (e.g. "Back to sign in"). */
  topSlot?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

export const AuthLayout = ({
  title,
  subtitle,
  topSlot,
  children,
  footer,
}: AuthLayoutProps): React.JSX.Element => {
  const branding = useBranding();

  return (
    <div className="bg-background flex min-h-screen flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        {/* Brand: logo + name */}
        <div className="mb-8 flex items-center justify-center gap-3">
          <span className="bg-primary flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl">
            {(branding.iconUrl ?? branding.logoUrl) ? (
              <img
                src={branding.iconUrl ?? branding.logoUrl ?? undefined}
                alt={branding.appName}
                className="h-6 w-6 object-contain"
              />
            ) : (
              <ShieldCheck className="text-primary-foreground h-5 w-5" aria-hidden />
            )}
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-heading text-foreground text-xl font-semibold tracking-tight">
              {branding.appName}
            </span>
            <span className="text-muted-foreground text-[12px] tracking-[0.14em] uppercase">
              {branding.shortName} Admin
            </span>
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          {topSlot}
          <h1 className="font-heading text-foreground text-2xl font-semibold tracking-tight">
            {title}
          </h1>
          {subtitle && <p className="text-muted-foreground text-sm">{subtitle}</p>}
        </div>

        <div className="mt-7">{children}</div>

        {footer && <div className="text-muted-foreground mt-7 text-center text-sm">{footer}</div>}
      </div>
    </div>
  );
};
